'use client'

import type { ChainId } from '@morpho-org/blue-sdk'
import type { Address } from 'viem'

import { useReadOnlyVault } from '@morpho-org/dapp-core'
import { cn } from '@morpho-org/ui-v6/utils'
import { Icon, Tooltip, TooltipContent, TooltipTrigger, TruncatedTooltip } from '@repo/ui'
import { Alert } from '@repo/ui/icons'
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'

import { V1Badge } from '../server/V1Badge'
import { Avatar, type AvatarSize } from './Avatar'
import { type Warning, WarningIcon } from './WarningIcon'

export interface VaultCellProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Vault contract address */
  address: Address
  /** Vault display name */
  name: string
  /** Token logo URI */
  logoURI?: string | null
  /** Avatar size (v5 parity, defaults to "medium") */
  logoSize?: AvatarSize
  /** Chain ID for read-only vault check */
  chainId: number
  /** Whether the vault is a V2 vault. Required: a missing flag would badge a V2 vault as V1. */
  isVaultV2: boolean
  /**
   * Tab index for the V1 badge and a clipped name. Defaults to -1 because the cell usually sits
   * inside a row link, where a focusable badge or name would add a tab stop per row. Pass 0 when
   * it does not.
   */
  badgeTabIndex?: number
  /** Warnings — hidden when the vault is read-only */
  warnings?: Warning[]
  /** Additional Tailwind classes for the name span (v5 TextVariant parity) */
  nameClassName?: string
  /** Extra content rendered at the end (e.g. user position indicator) */
  renderEnd?: ReactNode
}

/**
 * VaultCell — displays a vault's avatar, name, badges, and optional indicators.
 *
 * When the vault is read-only an alert icon replaces the warnings.
 *
 * @example
 * ```tsx
 * <VaultCell
 *   address="0x..."
 *   name="Steakhouse USDC"
 *   logoURI="https://cdn.morpho.org/tokens/usdc.png"
 *   chainId={1}
 *   isVaultV2
 *   warnings={[{ level: "YELLOW", message: "Short timelock" }]}
 *   renderEnd={<UserPositionIndicator />}
 * />
 * ```
 */
const VaultCell = forwardRef<HTMLDivElement, VaultCellProps>(
  (
    {
      address,
      name,
      logoURI,
      logoSize = 'medium',
      chainId,
      isVaultV2,
      badgeTabIndex = -1,
      warnings,
      nameClassName,
      renderEnd,
      className,
      ...props
    },
    ref
  ) => {
    const { message, isReadOnly } = useReadOnlyVault({
      // oxlint-disable-next-line typescript/consistent-type-assertions
      chainId: chainId as ChainId,
      address
    })

    return (
      <div ref={ref} className={cn('gap-xs flex min-w-0 items-center', className)} {...props}>
        {logoURI && <Avatar src={logoURI} size={logoSize} />}

        <TruncatedTooltip text={name}>
          {(nameRef, isTruncated) => (
            <span
              ref={nameRef}
              tabIndex={isTruncated ? badgeTabIndex : undefined}
              className={cn(
                'text-xs text-foreground min-w-0 truncate leading-normal',
                nameClassName
              )}
            >
              {name}
            </span>
          )}
        </TruncatedTooltip>

        {!isVaultV2 && <V1Badge tabIndex={badgeTabIndex} />}

        {isReadOnly ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="text-muted-foreground hover:text-foreground transition-colors">
                <Icon icon={<Alert />} />
              </div>
            </TooltipTrigger>
            <TooltipContent>{message}</TooltipContent>
          </Tooltip>
        ) : (
          warnings && <WarningIcon warnings={warnings} />
        )}

        {renderEnd}
      </div>
    )
  }
)
VaultCell.displayName = 'VaultCell'

export { VaultCell }
export default VaultCell
